import {observer} from 'mobx-react';
import React, {useContext, useEffect, useRef} from 'react';
import {useParams} from 'react-router-dom';
import {Container} from 'reactstrap';
import styled from 'styled-components';
import {MapContext} from '../../apiContexts';
import {useMap} from '../../components/map/hooks';
import useMapDevice from '../../components/map/hooks/useMapDevice';
import useMapAlarm from '../../components/map/hooks/useMapAlarm';
import useMapGeofences from '../../components/map/hooks/useMapGeofences';
import useMapLabel from '../../components/map/hooks/useMapLabel';
import useMapPosition from '../../components/map/hooks/useMapPosition';
import useMapReport from '../../components/map/hooks/useMapReport';
import useMapTrace from '../../components/map/hooks/useMapTraceV2';
import useDevices from '../../hooks/use-devices';
import useStore from '../../hooks/use-store';
import DeviceDetail from '../../views/event-map/device-detail';
import PopupEvent from '../../views/map/popupEvent';
import MapRightActions from '../../views/map-right-actions';
import {useIntl} from 'react-intl';
import MapLegend from '../../components/map-legend';
import useAlerts from '../../hooks/use-alerts';
import {notifyDeviceGoneAndReload} from '../../helpers/notifyDeviceGoneAndReload';

const Map = styled.div`
    height: 100%;
    width: 100%;
    position: relative;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    border-radius: 12px;

    .ol-viewport {
        border-radius: 12px;
    }

    #right-actions {
        position: absolute;
        right: 1rem;
        z-index: 10;
        top: 1rem;
    }
`;

const FORTALEZA_CENTER = [-38.5433, -3.7172];
const FORTALEZA_BOUNDS = {minLon: -38.65, maxLon: -38.40, minLat: -3.85, maxLat: -3.70};
const ALARM_TYPES = [
    'general', 'sos', 'vibration', 'movement', 'lowspeed', 'overspeed',
    'fallDown', 'lowPower', 'lowBattery', 'fault', 'powerOff', 'powerOn',
    'door', 'lock', 'unlock', 'geofence', 'geofenceEnter', 'geofenceExit',
    'gpsAntennaCut', 'accident', 'tow', 'idle', 'highRpm', 'hardAcceleration',
    'hardBraking', 'hardCornering', 'laneChange', 'fatigueDriving', 'powerCut',
    'powerRestored', 'jamming', 'temperature', 'parking', 'shock', 'bonnet',
    'footBrake', 'fuelLeak', 'tampering', 'removing',
];

const AdminMapContainer = (props) => {
    const {store} = useStore();
    const {events, customerStore} = store;
    const {eventId} = useParams();
    const {devices} = useDevices();
    const mapRef = useRef();
    const intl = useIntl();
    const {toast} = useAlerts();

    const {
        setSelectedRow,
    } = useContext(MapContext);

    const {
        instance,
        trace,
        selectedDeviceTrace,
        toggleTrace,
        toggleSelectedDeviceTrace,
        label,
        toggleLabel,
        selectedMap,
        setSelectedMap,
        showDevices,
        setShowDevices: toggleDevice,
        showGeofences,
        setShowGeofences,
        follow,
        setFollow,
        isAlarmsVisible,
        toggleAlarmsVisibility,
        createGeofenceForCurrentDevice,
    } = useMap(
        {
            target: mapRef,
            center: store.map.olCenter,
            zoom: store.map.zoom,
            tiers: store.map.olCustomTiles,
            initAllDevices: true,
            centerOnCluster: true,
            cluster: {
                distance: 25,
            },
        },
        useMapDevice,
        useMapPosition,
        useMapTrace,
        useMapLabel,
        useMapReport,
        useMapGeofences,
        useMapAlarm,
    );

    const followToggle = () => {
        const status = !follow;
        setFollow(status);
        if (status) {
            devices.setSelected(events.currentDevice.id);
            const {longitude, latitude} = events.currentDevice.currentPosition;
            instance.goTo([longitude, latitude]);
        }
    };

    const simulateAlarms = () => {
        if (!instance) return;
        instance.removeOverlays('alarm');
        instance.goTo(FORTALEZA_CENTER, 12);
        ALARM_TYPES.forEach((alarmType, i) => {
            const lon = FORTALEZA_BOUNDS.minLon + Math.random() * (FORTALEZA_BOUNDS.maxLon - FORTALEZA_BOUNDS.minLon);
            const lat = FORTALEZA_BOUNDS.minLat + Math.random() * (FORTALEZA_BOUNDS.maxLat - FORTALEZA_BOUNDS.minLat);
            instance.addAlarmOverlay(`sim-${i}`, alarmType, [lon, lat]);
        });
    };

    useEffect(() => {
        if (instance && devices.list.length) {
            const map = instance.instance;
            instance.setI18n(intl);

            map.on('click', (event) => {
                if (events.currentDevice !== null) {
                    devices.setSelected(null);
                    events.setCurrentDevice(null);

                    return;
                }

                const [feature, layer] = map.forEachFeatureAtPixel(event.pixel, (feature, layer) => {
                    return [feature, layer];
                }) || [];

                if (!layer) return;

                const layerName = layer.get('name');

                if (layerName === 'reportLayer') {
                    const featureName = feature.get('name');
                    if (featureName !== 'line') {
                        setSelectedRow({
                            id: feature.get('id') || feature.getId(),
                            scroll: true,
                        });
                    }
                }

                if (layerName === 'devicesLayer') {
                    const features = feature.get('features');
                    if (features.length === 1) {
                        const deviceId = features[0].getId();
                        const device = store.devices.findById(+deviceId);
                        if (!device) {
                            notifyDeviceGoneAndReload(toast, intl);
                            return;
                        }
                        devices.setSelected(deviceId);
                        events.setCurrentDevice(+deviceId);

                        (async () => {
                            const position = device.currentPosition;

                            if (!position) return;

                            const {longitude, latitude} = position;
                            instance.goTo([longitude, latitude], 18);
                        })();
                    } else {
                        instance.spiderfyCluster(feature);
                    }
                }
            });

            map.on('pointermove', (event) => {
                map.getTargetElement().style.cursor = map.hasFeatureAtPixel(event.pixel) ?
                    'pointer' :
                    '';
            });

            map.on('pointerdrag', (event) => {
                instance.isFollowAnimating = false;
                setFollow(false);
            });

            let zoomCache;
            map.on('movestart', (event) => {
                zoomCache = event.map.getView().getZoom();
            });

            map.on('moveend', (event) => {
                if (instance.isFollowAnimating) {
                    instance.isFollowAnimating = false;
                    return;
                }
                const zoom = event.map.getView().getZoom();
                zoom !== zoomCache && setFollow(false);
            });
        }
    }, [instance, devices.list.length]);

    const toggleGeofences = () => {
        setShowGeofences(!showGeofences);
    };

    const onChangeMap = (value) => {
        setSelectedMap(value);
    };

    const getDrive = () => {
        return events.currentDevice.currentPosition && events.currentDevice.currentPosition.attributes ?
            store.drivers.getDriverByUniqueId(events.currentDevice.currentPosition.attributes.driverUniqueId) : null;
    };

    const getCustomer = () => {
        return customerStore.findById(events.currentDevice.customerId);
    };

    return (
        <Container fluid className="px-4 pb-4 position-relative h-100">
            <MapLegend/>
            <>
                {devices.list.length > 0 && <PopupEvent isEvent={!!eventId}/>}

                {devices.list.length > 0 && events.currentDevice && events.currentDevice.currentPosition && (
                    <DeviceDetail
                        toggleTrace={toggleSelectedDeviceTrace}
                        trace={selectedDeviceTrace}
                        createGeofence={createGeofenceForCurrentDevice}
                        followToogle={followToggle}
                        follow={follow}
                        driver={getDrive()}
                        customer={getCustomer()}
                    />
                )}
                <Map ref={mapRef} className="custom-shadow-2">
                    <MapRightActions
                        map={instance}
                        toggleTrace={toggleTrace}
                        trace={trace}
                        toggleLabel={toggleLabel}
                        isAlarmsVisible={isAlarmsVisible}
                        toggleAlarmsVisibility={toggleAlarmsVisibility}
                        simulateAlarms={store.session.administrator ? simulateAlarms : undefined}
                        label={label}
                        onChangeMap={onChangeMap}
                        selectedMap={selectedMap}
                        toggleDevice={toggleDevice}
                        showDevices={showDevices}
                        toggleGeofences={toggleGeofences}
                        showGeofences={showGeofences}
                        close={'/admin'}
                    />
                </Map>
            </>
        </Container>
    );
};

export default observer(AdminMapContainer);
